import { useState, useEffect, useRef, useCallback } from 'react';
import API from '@/lib/api';
import { X, MagnifyingGlass, UserCircle } from '@phosphor-icons/react';

/**
 * MailContactAutocomplete — chip-based e-mail input met live autocomplete.
 *
 * Props:
 *   value: string (komma/puntkomma-gescheiden lijst, of array van emails)
 *   onChange: (newValueString) => void
 *   placeholder?: string
 *   testId?: string
 *
 * Backend: GET /api/mail/contacts?q=...&limit=20
 */
const SOURCE_LABELS = {
  klant:       { label: 'Klant',       bg: 'bg-green-100',   text: 'text-green-700' },
  leverancier: { label: 'Leverancier', bg: 'bg-blue-100',    text: 'text-blue-700' },
  verkoper:    { label: 'Verkoper',    bg: 'bg-purple-100',  text: 'text-purple-700' },
  recent:      { label: 'Recent',      bg: 'bg-amber-100',   text: 'text-amber-700' },
};

export default function MailContactAutocomplete({ value, onChange, placeholder, testId }) {
  const stringToList = (s) => (s || '').split(/[,;]/).map(x => x.trim()).filter(x => x.length > 0);
  const listToString = (arr) => arr.join(', ');
  const initialList = Array.isArray(value) ? value : stringToList(value);
  const [chips, setChips] = useState(initialList);
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Sync chips → bovenliggende state
  useEffect(() => {
    onChange(listToString(chips));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chips]);

  // Sync bovenliggende state-changes naar chips (bv. reply prefill)
  useEffect(() => {
    const next = Array.isArray(value) ? value : stringToList(value);
    setChips(prev => JSON.stringify(prev) !== JSON.stringify(next) ? next : prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Sluit dropdown bij click buiten component
  useEffect(() => {
    const h = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Debounced search
  const searchContacts = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await API.get('/mail/contacts', { params: { q, limit: 15 } });
        // Filter contacten die al in chips zitten
        const filtered = (r.data || []).filter(c => !chips.includes(c.email));
        setSuggestions(filtered);
        setHighlightIdx(0);
      } catch {
        setSuggestions([]);
      }
      setLoading(false);
    }, 180);
  }, [chips]);

  const onInputChange = (e) => {
    const v = e.target.value;
    setInput(v);
    setShowDropdown(true);
    if (v.trim().length >= 1) {
      searchContacts(v.trim());
    } else {
      // Leeg input → toon alle recente contacten
      searchContacts('');
    }
  };

  const addChip = (email) => {
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !cleaned.includes('@')) return;
    if (chips.includes(cleaned)) {
      setInput(''); return;
    }
    setChips(prev => [...prev, cleaned]);
    setInput('');
    setSuggestions([]);
    setShowDropdown(false);
    // refocus input voor snel meerdere toevoegen
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeChip = (email) => setChips(prev => prev.filter(e => e !== email));

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && suggestions.length > 0 && suggestions[highlightIdx]) {
        addChip(suggestions[highlightIdx].email);
      } else if (input.includes('@')) {
        addChip(input);
      }
    } else if (e.key === ',' || e.key === ';' || e.key === ' ' && input.includes('@')) {
      if (input.includes('@')) {
        e.preventDefault();
        addChip(input);
      }
    } else if (e.key === 'Backspace' && input === '' && chips.length > 0) {
      // Backspace op leeg input → verwijder laatste chip
      setChips(prev => prev.slice(0, -1));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showDropdown && suggestions.length > 0) {
        setHighlightIdx(i => Math.min(i + 1, suggestions.length - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showDropdown && suggestions.length > 0) {
        setHighlightIdx(i => Math.max(i - 1, 0));
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const onFocus = () => {
    setShowDropdown(true);
    if (suggestions.length === 0) searchContacts(input.trim());
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId || 'mail-contact-autocomplete'}>
      <div className="field-input flex flex-wrap items-center gap-1 cursor-text min-h-[38px]" onClick={() => inputRef.current?.focus()}>
        {chips.map(email => (
          <span
            key={email}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#EEF3FF] border border-[#002FA7]/30 rounded text-xs text-[#002FA7]"
            data-testid={`mail-contact-chip-${email}`}
          >
            <UserCircle size={11} weight="fill" />
            <span>{email}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeChip(email); }}
              className="hover:text-red-600 text-[#002FA7]/60"
              tabIndex={-1}
            >
              <X size={11} weight="bold" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          placeholder={chips.length === 0 ? (placeholder || 'Typ naam of e-mailadres...') : ''}
          className="flex-1 min-w-[140px] outline-none border-0 bg-transparent text-sm"
          autoComplete="off"
        />
      </div>
      {showDropdown && (suggestions.length > 0 || loading) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 max-h-72 overflow-y-auto" data-testid="mail-contact-suggestions">
          {loading && suggestions.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400 inline-flex items-center gap-1.5">
              <MagnifyingGlass size={11} /> Zoeken...
            </div>
          )}
          {suggestions.map((c, idx) => {
            const src = SOURCE_LABELS[c.source] || { label: c.source, bg: 'bg-gray-100', text: 'text-gray-600' };
            const isHi = idx === highlightIdx;
            return (
              <button
                key={c.email + idx}
                type="button"
                onClick={() => addChip(c.email)}
                onMouseEnter={() => setHighlightIdx(idx)}
                className={`w-full text-left px-3 py-1.5 flex items-center justify-between gap-2 border-b border-gray-100 ${isHi ? 'bg-[#EEF3FF]' : 'hover:bg-gray-50'}`}
                data-testid={`mail-contact-suggestion-${idx}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-900 truncate">{c.naam || c.email}</div>
                  <div className="text-[10px] text-gray-500 truncate">{c.email}</div>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded ${src.bg} ${src.text} flex-shrink-0`}>
                  {src.label}
                </span>
              </button>
            );
          })}
          {!loading && suggestions.length === 0 && input.length > 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">
              Geen contact gevonden. {input.includes('@') && <span>Druk op <kbd className="px-1 py-0.5 bg-gray-100 rounded">Enter</kbd> om "{input}" toe te voegen.</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
